import React from 'react';
import { abgGraphqlClient, ProxyGraphClients } from '@atlassian/temp-client-utils';
import { generatedTypesNew } from '@css-xp/graphtypes';
import { DEFAULT_PAGE_SIZE, SUBSCRIPTION_CONSTANTS } from '@css-xp/ppc-constants';
import { usePersistedCacheQuery } from '@css-xp/ppc-custom-hooks';
import { fetchProductsForTransactionAccountPPC } from './queries/ans-queries/fetch-product-for-transaction-account-ppc';
import { fetchInternalLicensesForTransactionAccountPPC } from './queries/ans-queries/fetch-internal-licenses-for-transaction-account-ppc';

const defaultSortOptions = {
  sortFields: [SUBSCRIPTION_CONSTANTS.SORT_KEYS.RENEW_DATE, SUBSCRIPTION_CONSTANTS.SORT_KEYS.ID],
  sortFieldsDirections: [generatedTypesNew.SortDirection.ASC, generatedTypesNew.SortDirection.ASC],
};

export const useFetchProductsForTransactionAccountQuery = ({
  txaId,
  sortOptions = defaultSortOptions,
  statusFilter = [],
  additionalFilterOptions = [],
  skipOption = false,
  isSandboxSupportForSubscriptionsEnabled = false,
  isSupportForExternallyBilledSubscriptionsEnabled = false,
  isTWCPhase1Enabled = false,
  shouldFetchInternalLicenses = false,
  relatedDomains,
  showEnterpriseInstances = false,
}: {
  txaId: string;
  sortOptions?: generatedTypesNew.SortType;
  statusFilter?: generatedTypesNew.FilterOption[];
  additionalFilterOptions?: generatedTypesNew.FilterOption[];
  skipOption?: boolean;
  isSandboxSupportForSubscriptionsEnabled?: boolean;
  isSupportForExternallyBilledSubscriptionsEnabled?: boolean;
  isTWCPhase1Enabled?: boolean;
  shouldFetchInternalLicenses?: boolean;
  relatedDomains?: string[];
  showEnterpriseInstances?: boolean;
}) => {
  const ansClient = React.useMemo(() => abgGraphqlClient(ProxyGraphClients.ANS), []);

  return usePersistedCacheQuery<any, any>(
    shouldFetchInternalLicenses ? fetchInternalLicensesForTransactionAccountPPC : fetchProductsForTransactionAccountPPC,
    {
      variables: {
        id: txaId,
        searchOptions: {
          first: DEFAULT_PAGE_SIZE,
          offset: 0,
          sort: sortOptions,
        },
        filterOptions: [
          {
            key: 'not',
            value: JSON.stringify([
              SUBSCRIPTION_CONSTANTS.addOnsFilter,
              SUBSCRIPTION_CONSTANTS.appsFilter,
              ...(isSandboxSupportForSubscriptionsEnabled ? [] : [SUBSCRIPTION_CONSTANTS.sandboxFilter]),
              ...(isTWCPhase1Enabled
                ? [
                    SUBSCRIPTION_CONSTANTS.collectionFilter,
                    ...(showEnterpriseInstances ? [] : [SUBSCRIPTION_CONSTANTS.enterpriseFilter]),
                  ]
                : []),
            ]),
          },
          ...statusFilter,
          ...additionalFilterOptions,
        ],
        isRestricted: false,
        fetchIsExternallyEnabled: isSupportForExternallyBilledSubscriptionsEnabled,
        ...(shouldFetchInternalLicenses && { relatedDomains: relatedDomains }),
      },
      skip: skipOption,
      client: ansClient,
    }
  );
};
